// Wrapper-side session manager — .
//
// Owns the RTC peer connection + data channel + TranslatorClient for
// the lifetime of the tab. The initial connect and each mid-session
// reconnect use the SAME `establish()` closure: a full session
// rebuild — fresh `/v1/connect` (new `session_id`, `signal_token`,
// `ice_servers`), fresh `RTCPeerConnection`, fresh SDP exchange,
// fresh data channel. A reconnect is not a poll of the old state —
// an `RTCPeerConnection` in `"failed"` is terminal, TURN creds
// expire, and coord tears down the old session row as soon as the
// box's control connection drops. Anything less than a full rebuild
// deadlocks (see task).
//
// Reconnect protocol:
//
//   * Listen for `pc.connectionState ∈ {"disconnected", "failed",
//     "closed"}` **and** `dc` `close` events. Either is enough to
//     trip a reconnect; both can fire in quick succession.
//   * On drop: close the old `pc`, wait `backoff(attempt)` ms, then
//     call `establish({phase})` again. Exponential with jitter:
//     `min(INITIAL * 2^(attempt-1), CAP) * (0.8 + random*0.4)`.
//   * Give up after `MAX_ATTEMPTS` attempts or `MAX_TOTAL_MS` elapsed
//     since the first failure, whichever comes first. Flip the status
//     observer to `"offline"` and surface `box_offline` (caller's
//     responsibility via `onStatus`).
//
// Diagnostic trace: each reconnect attempt emits
// `p2claw:reconnect:<phase>` breadcrumbs via `console.info` so a
// real-world failure leaves a grep-able trail. Sub-steps inside
// `establish` route through the `phase` callback in
// `EstablishContext` (set only during reconnects, not the initial
// connect). Phases: `start`, `attempt N`, `connect_ok session=<id>`,
// `pc_new`, `dc_open`, `success attempt=N`, `failed reason=...`,
// `giving_up wall=<s>s attempt=N`.
//
// The session exposes a stable `TranslatorClientLike` handle that
// forwards `request` / `openWebSocket` / `closeWebSocket` calls to the
// *current* live TranslatorClient. Downstream subsystems (sw-handoff,
// ws-bridge) hold this handle instead of a raw `TranslatorClient` so
// their wiring survives a reconnect; requests in-flight at the moment
// of drop fail with a generic transport-closed error, which app code
// sees as a regular fetch failure (accurate: the fetch really did
// fail).

import {
  collectDcStats,
  type DcStatsSummary,
  type PeerConnectResult,
} from "../webrtc/connection.js";
import {
  TranslatorClient,
  type ByteTransport,
  type TranslatorRequest,
  type TranslatorResponse,
} from "../translator/client.js";
import type { Frame } from "../wire/index.js";
import { pickFrameBodyCap } from "./path-cap.js";

/** Narrow subset of `TranslatorClient` the downstream consumers use. */
export interface TranslatorClientLike {
  request(req: TranslatorRequest): Promise<TranslatorResponse>;
  openWebSocket(input: {
    path: string;
    headers: Array<readonly [string, string]>;
    onFrame: (frame: Frame) => void;
  }): { streamId: number; send: (frame: Frame) => void | Promise<void> };
  closeWebSocket(streamId: number): void;
  /**  optional rolling RX-frame snapshot. The SW handoff's
   *  stall watchdog calls this and dumps the totals into its
   *  diagnostic log so a trace shows what arrived on the data
   *  channel before the body pump gave up. Optional so tests with
   *  ad-hoc translator fakes don't have to stub it. */
  rxSummary?(): {
    res: number;
    data: number;
    end: number;
    err: number;
    ws_accept: number;
    ws_msg: number;
    ws_close: number;
    other: number;
    bytes: number;
    orphanRes: number;
    orphanData: number;
  };
  /**  optional WebRTC stats snapshot. The stall watchdog
   *  calls this to dump per-channel + sctp + transport + selected
   *  candidate-pair counters from `pc.getStats(dc)`. Resolves to
   *  `null` when the current peer is gone (mid-reconnect or
   *  post-dispose). Optional so the same `TranslatorClientLike`
   *  shape works for tests that don't model WebRTC. */
  getRtcStats?(): Promise<DcStatsSummary | null>;
}

/** Status transitions emitted via `onStatus`. */
export type SessionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "offline";

/** 2s initial backoff, ×2 each failure, capped at 30s. Schedule per
 *   reconnect overlay: 2 → 4 → 8 → 16 → 30 → 30 …
 *  with ±20% jitter. */
export const INITIAL_BACKOFF_MS = 2_000;
export const CAP_BACKOFF_MS = 30_000;
/** Surface `box_offline` after this many attempts OR … */
export const MAX_ATTEMPTS = 20;
/** … this many ms since the session first dropped. Whichever is first.
 *  5 minutes matches the overlay's "give up and swap in box_offline"
 *  wall per the reconnect-overlay spec. */
export const MAX_TOTAL_MS = 300_000;
/** Hard timeout for one `establish()` attempt. Without this, a hung
 *  signaling / ICE / DC-open await inside `connectToBox` stalls the
 *  entire reconnect loop — `establish` never resolves, the loop never
 *  moves on to the next attempt, the overlay sits forever. 30s is
 *  generous: a healthy reconnect completes in <5s end-to-end, and a
 *  legitimately slow ICE gather should still fit comfortably. */
export const ATTEMPT_TIMEOUT_MS = 30_000;

/**
 * Context handed to `establish` on each call. The `phase` callback
 * is present on reconnect attempts so sub-steps (`connect_ok`,
 * `pc_new`, `dc_open`) can emit diagnostic breadcrumbs; `undefined`
 * on the initial-connect path (that surface goes through the
 * wrapper boot's regular logging).
 */
export interface EstablishContext {
  phase?: (phase: string, detail?: string) => void;
  /** Aborted when the per-attempt timeout fires. `establish`
   *  implementations MUST propagate this into any awaits that can
   *  hang (signaling WS, ICE gather, datachannel open) so a stalled
   *  attempt gives up cleanly and the reconnect loop can try again. */
  signal?: AbortSignal;
}

export interface OpenSessionOptions {
  /**
   * Full session-establishment sequence. Called once for the
   * initial connect and again for every reconnect attempt — every
   * call MUST mint a fresh `session_id`, ICE servers, and
   * `RTCPeerConnection`. (A stale session_id causes coord to
   * reject the signaling WS; a stale PC in `"failed"` state can't
   * be revived; stale TURN creds expire.) Throws to signal
   * failure; the reconnect loop retries with exponential backoff
   * until exhaustion.
   */
  establish(ctx: EstablishContext): Promise<PeerConnectResult>;
  /** Build the `ByteTransport` from a successful `PeerConnectResult`. */
  makeTransport(session: PeerConnectResult): ByteTransport;
  /** Status observer. Called synchronously on every transition. */
  onStatus?(status: SessionStatus, info?: { attempt?: number }): void;
  /** Testing seam — defaults to `globalThis.setTimeout`. Only used
   *  for the backoff sleep between attempts. The per-attempt
   *  timeout uses `globalThis.setTimeout` directly so tests with
   *  mocked timers aren't polluted with timers they didn't ask for;
   *  the 30s real-wall deadline never fires within a test run, which
   *  is what we want for tests that don't specifically target the
   *  timeout path. */
  setTimeoutFn?(cb: () => void, ms: number): unknown;
  /** Override the per-attempt establish timeout. Defaults to
   *  `ATTEMPT_TIMEOUT_MS` (30s). Tests that want to exercise the
   *  timeout path can lower this. */
  attemptTimeoutMs?: number;
  /** Testing seam — defaults to `Date.now`. */
  now?(): number;
  /** Testing seam — defaults to `Math.random`. */
  random?(): number;
  /** Testing seam — defaults to `console.info`. Hooked so unit
   *  tests can assert the diagnostic trace without polluting stdout. */
  logger?: (message: string, detail?: string) => void;
  /** Mobile-background pause hooks. When the tab is hidden,
   *  iOS Safari / Android Chrome aggressively suspend timers and
   *  may tear down the DC silently. Burning retry budget while
   *  hidden surfaces `box_offline` the moment the user returns to
   *  the tab — exactly the bug we're fixing.
   *
   *  `isHidden`: returns true while the tab is in the background.
   *  Defaults to `() => false` so non-DOM tests keep the original
   *  behavior. The browser-side wires it to
   *  `() => document.visibilityState === "hidden"`.
   *
   *  `awaitVisible`: resolves once the tab is visible again. Used
   *  to park the reconnect loop while hidden — better than burning
   *  exponential backoff on a tab the user can't see. The visible-
   *  again wakeup also resets the retry budget (`attempt` /
   *  `firstDropAt`) so the user gets a fresh batch of attempts when
   *  they return.
   *
   *  Default: instant resolve — the original behavior. */
  isHidden?: () => boolean;
  awaitVisible?: () => Promise<void>;
}

export interface SessionHandle {
  /** Stable facade. Forwards to the current live TranslatorClient. */
  translator: TranslatorClientLike;
  /**
   * Current connect attempt count since the last successful connect.
   * 0 during steady-state; ≥ 1 while backing off; frozen at the count
   * that tripped `"offline"` on exhaustion.
   */
  attemptCount(): number;
  /** True iff there's a live `RTCPeerConnection` in `connected`
   *  state with an `open` data channel. Used by wrapper-side
   *  visibility listeners to decide whether a return-to-foreground
   *  needs to kick a reconnect. */
  isAlive(): boolean;
  /**
   * Force the reconnect loop to run NOW. Resets the retry
   * budget — caller is signaling "the situation just changed,
   * give it a fresh batch of attempts" (e.g. user returned from
   * background). Behavior:
   *
   *   * If the loop is already running, just reset `attempt` and
   *     `firstDropAt` so the next iteration uses a fresh budget.
   *   * If the loop has given up (`status === "offline"`), restart
   *     it.
   *   * If the session is steady-state (DC alive), trigger a fresh
   *     drop+reconnect cycle. (Caller is asserting they have outside
   *     evidence the connection is dead — usually a
   *     `visibilitychange` after a long background period where
   *     mobile may have torn the DC down without firing the
   *     close events JS would normally see.)
   *
   *   No-op if disposed.
   */
  kickReconnect(reason: string): void;
  /** Tear down the session permanently. Cancels any pending reconnect. */
  dispose(): void;
}

/**
 * Open a session. The returned promise resolves after the **initial**
 * connect succeeds (so callers can proceed with iframe mount etc.).
 * Subsequent reconnects happen asynchronously behind the stable
 * `translator` handle and are reported via `onStatus`.
 *
 * The initial connect's failure rejects the returned promise — the
 * wrapper surfaces that upward as a bootstrap failure, not a
 * reconnect.
 */
export async function openSession(
  opts: OpenSessionOptions,
): Promise<SessionHandle> {
  const setTimeoutFn =
    opts.setTimeoutFn ??
    ((cb: () => void, ms: number) => setTimeout(cb, ms));
  const attemptTimeoutMs = opts.attemptTimeoutMs ?? ATTEMPT_TIMEOUT_MS;
  const nowFn = opts.now ?? (() => Date.now());
  const randomFn = opts.random ?? (() => Math.random());
  const status = opts.onStatus ?? (() => {});
  const log =
    opts.logger ??
    ((msg: string, detail?: string) =>
      detail === undefined ? console.info(msg) : console.info(msg, detail));
  // Visibility hooks. Defaulting both to "always visible /
  // never wait" preserves the original test behavior — non-DOM tests
  // don't suddenly start parking the reconnect loop on a hidden
  // signal that doesn't exist.
  const isHidden = opts.isHidden ?? (() => false);
  const awaitVisible = opts.awaitVisible ?? (() => Promise.resolve());

  status("connecting", { attempt: 0 });
  const initial = await opts.establish({});
  let currentPeer: PeerConnectResult | null = initial;
  // Pick the per-session DATA-frame body cap from the selected ICE
  // candidate pair. Default-conservative: any uncertainty (ts-v6
  // pair, stats not yet stable, getStats throws) returns the ~800-B
  // IP/MTU-safe cap. Only a positively-confirmed non-ts-v6 pair
  // unlocks the full 16-KiB SCTP cap. A detection miss caps slower
  // but never wedges.
  const initialCapPick = pickFrameBodyCap(initial.pc);
  const initialCap =
    typeof initialCapPick === "number" ? initialCapPick : await initialCapPick;
  let currentTranslator: TranslatorClient = new TranslatorClient(
    opts.makeTransport(initial),
    { maxFrameBodyBytes: initialCap },
  );
  status("connected", { attempt: 0 });

  let attempt = 0;
  let firstDropAt: number | null = null;
  let reconnecting = false;
  let disposed = false;
  /** Cleanup for the drop listeners on the *current* pc/dc. */
  let removeDropListeners: (() => void) | null = null;

  const handle: TranslatorClientLike = {
    request(req) {
      return currentTranslator.request(req);
    },
    openWebSocket(input) {
      return currentTranslator.openWebSocket(input);
    },
    closeWebSocket(streamId) {
      currentTranslator.closeWebSocket(streamId);
    },
    rxSummary() {
      return currentTranslator.rxSummary();
    },
    async getRtcStats() {
      // surface the current pc + dc to the stall watchdog
      // diagnostic. Returns `null` if we're mid-reconnect (no
      // current peer) so the caller logs cleanly without throwing.
      const peer = currentPeer;
      if (peer === null) return null;
      return collectDcStats(peer.pc, peer.dataChannel);
    },
  };

  function onDrop(reason: string): void {
    if (disposed || reconnecting) return;
    reconnecting = true;
    removeDropListeners?.();
    removeDropListeners = null;
    // Close the old pc; downstream subscribers of the translator's
    // transport see the `onClose` and the pending requests reject.
    try {
      currentPeer?.pc.close();
    } catch {
      /* ignore */
    }
    currentPeer = null;
    if (firstDropAt === null) firstDropAt = nowFn();
    void runReconnect(reason);
  }

  async function runReconnect(initialReason: string): Promise<void> {
    log("p2claw:reconnect:start", `reason=${initialReason}`);
    while (!disposed) {
      // Park while hidden. On mobile, attempts run during
      // background suspension don't actually do useful work — the
      // browser may not even schedule the timers — and they burn
      // through the retry budget so a return-to-foreground lands on
      // `box_offline` instead of a healthy reconnect. Wait for
      // visible, then reset the budget so the user gets a fresh
      // batch of attempts.
      if (isHidden()) {
        log("p2claw:reconnect:paused", "reason=hidden");
        await awaitVisible();
        if (disposed) return;
        log("p2claw:reconnect:resumed", `attempt=${attempt}->0`);
        attempt = 0;
        firstDropAt = nowFn();
      }
      attempt += 1;
      const elapsed = firstDropAt === null ? 0 : nowFn() - firstDropAt;
      if (attempt > MAX_ATTEMPTS || elapsed > MAX_TOTAL_MS) {
        log(
          "p2claw:reconnect:giving_up",
          `wall=${Math.round(elapsed / 1000)}s attempt=${attempt}`,
        );
        status("offline", { attempt });
        reconnecting = false;
        return;
      }
      status("reconnecting", { attempt });
      log("p2claw:reconnect:attempt", String(attempt));
      await sleep(backoffMs(attempt, randomFn), setTimeoutFn);
      if (disposed) return;
      // Per-attempt phase logger. Routed through the same `log`
      // seam so tests can assert the sub-step trail without a global
      // console spy.
      const phase = (p: string, detail?: string): void => {
        log(`p2claw:reconnect:${p}`, detail);
      };
      // Per-attempt AbortController. Fires if `establish` hasn't
      // resolved inside `attemptTimeoutMs`. Without this the loop
      // wedges forever when any inner await inside `connectToBox`
      // hangs (the signaling `auth_ack` that never arrives, ICE
      // that silently fails to converge, a datachannel the box
      // never opens). Every inner await in the handshake honors
      // this signal: `SignalingClient.open`, `waitForConnected`,
      // `waitForDataChannel`, `waitForDataChannelOpen` — so when
      // the abort fires, establish rejects, the catch block below
      // logs the attempt, and the loop moves on.
      //
      // Uses `globalThis.setTimeout` (not `setTimeoutFn`) so test
      // mocks for backoff don't pick up a timer they didn't
      // schedule. Tests that want to exercise the timeout path
      // lower `attemptTimeoutMs` to a small value and wait real
      // time.
      const abort = new AbortController();
      let timedOut = false;
      const timeoutId = globalThis.setTimeout(() => {
        timedOut = true;
        abort.abort();
      }, attemptTimeoutMs);
      try {
        const fresh = await opts.establish({ phase, signal: abort.signal });
        globalThis.clearTimeout(timeoutId);
        if (disposed) {
          try {
            fresh.pc.close();
          } catch {
            /* ignore */
          }
          return;
        }
        currentPeer = fresh;
        const capPick = pickFrameBodyCap(fresh.pc);
        const cap = typeof capPick === "number" ? capPick : await capPick;
        currentTranslator = new TranslatorClient(opts.makeTransport(fresh), {
          maxFrameBodyBytes: cap,
        });
        installDropListeners(fresh);
        log("p2claw:reconnect:success", `attempt=${attempt}`);
        attempt = 0;
        firstDropAt = null;
        reconnecting = false;
        status("connected", { attempt: 0 });
        return;
      } catch (e) {
        globalThis.clearTimeout(timeoutId);
        const reason = timedOut
          ? `timeout after ${attemptTimeoutMs}ms`
          : `${(e as Error).message ?? String(e)}`;
        log("p2claw:reconnect:failed", `reason=${reason}`);
        // Loop again — next iteration checks attempt/elapsed limits
        // before scheduling another sleep.
      }
    }
  }

  function installDropListeners(peer: PeerConnectResult): void {
    const onPcChange = (): void => {
      const s = peer.pc.connectionState;
      if (s === "disconnected" || s === "failed" || s === "closed") {
        onDrop(`pc.connectionState=${s}`);
      }
    };
    const onDcClose = (): void => {
      onDrop("dc.close");
    };
    peer.pc.addEventListener("connectionstatechange", onPcChange);
    peer.dataChannel.addEventListener("close", onDcClose);
    removeDropListeners = () => {
      peer.pc.removeEventListener("connectionstatechange", onPcChange);
      peer.dataChannel.removeEventListener("close", onDcClose);
    };
  }

  installDropListeners(initial);

  function isAlive(): boolean {
    if (disposed) return false;
    if (!currentPeer) return false;
    if (currentPeer.pc.connectionState !== "connected") return false;
    if (currentPeer.dataChannel.readyState !== "open") return false;
    return true;
  }

  function kickReconnect(reason: string): void {
    if (disposed) return;
    if (reconnecting) {
      // Mid-loop. Reset budget so the next iteration uses a fresh
      // batch of attempts — caller's signaling that the situation
      // changed (e.g. `visibilitychange` after backgrounding).
      attempt = 0;
      firstDropAt = nowFn();
      log("p2claw:reconnect:kicked", `reason=${reason} mode=reset`);
      return;
    }
    // Steady-state OR offline (gave up). `onDrop` is a no-op when
    // `reconnecting` is true, but we already short-circuited that
    // above. Setting `firstDropAt = null` and `attempt = 0` here
    // gives the upcoming reconnect a fresh budget; `onDrop` itself
    // sets `firstDropAt = nowFn()` if it was null, so this is
    // consistent.
    attempt = 0;
    firstDropAt = null;
    log("p2claw:reconnect:kicked", `reason=${reason} mode=force`);
    onDrop(reason);
  }

  return {
    translator: handle,
    attemptCount: () => attempt,
    isAlive,
    kickReconnect,
    dispose(): void {
      if (disposed) return;
      disposed = true;
      removeDropListeners?.();
      removeDropListeners = null;
      try {
        currentPeer?.pc.close();
      } catch {
        /* ignore */
      }
      currentPeer = null;
    },
  };
}

/** Exported for tests that want to verify the schedule directly. */
export function backoffMs(
  attempt: number,
  random: () => number = Math.random,
): number {
  const base = Math.min(
    INITIAL_BACKOFF_MS * Math.pow(2, Math.max(0, attempt - 1)),
    CAP_BACKOFF_MS,
  );
  // ±20% jitter: multiply by [0.8, 1.2).
  const jitter = 0.8 + random() * 0.4;
  return Math.round(base * jitter);
}

function sleep(
  ms: number,
  setTimeoutFn: (cb: () => void, ms: number) => unknown,
): Promise<void> {
  return new Promise<void>((resolve) => {
    setTimeoutFn(() => resolve(), ms);
  });
}
